import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { usePartnerContext } from '@/contexts/PartnerContext';
import { toast } from 'sonner';
import { Holding, AccountType, AssetClass } from '@/types/portfolio';

// Type guard for error objects
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unknown error occurred';
}

type ViewMode = 'individual' | 'partner' | 'household';
type RelationshipType = 'spouse' | 'partner' | 'financial-partner';
type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export interface Partner {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  relationshipType: RelationshipType;
}

export interface PartnerInvitation {
  id: string;
  inviterId: string;
  inviteeEmail: string;
  relationshipType: RelationshipType;
  status: InvitationStatus;
  message?: string;
  expiresAt: string;
  createdAt: string;
}

interface DbHolding {
  id: string;
  user_id: string;
  ticker: string;
  name: string | null;
  shares: number;
  current_price: number;
  cost_basis: number | null;
  account_type: string | null;
  asset_class: string | null;
  expense_ratio: number | null;
}

const transformHolding = (dbHolding: DbHolding): Holding => ({
  id: dbHolding.id,
  ticker: dbHolding.ticker,
  name: dbHolding.name || dbHolding.ticker,
  shares: dbHolding.shares,
  currentPrice: dbHolding.current_price,
  costBasis: dbHolding.cost_basis || dbHolding.current_price,
  accountType: (dbHolding.account_type as AccountType) || 'Taxable',
  assetClass: (dbHolding.asset_class as AssetClass) || 'US Stocks',
  expenseRatio: dbHolding.expense_ratio || undefined,
});

export function usePartner() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [currentView, setCurrentView] = useState<ViewMode>('individual');
  const { samplePartner, sampleHoldings, setSamplePartner: contextSetSamplePartner, clearSamplePartner: contextClearSamplePartner } = usePartnerContext();

  // 1. Query for Partner Data (Link + Profile + Invitations)
  const { data: partnerData, isLoading: partnerLoading, refetch: refetchPartner } = useQuery({
    queryKey: ['partner', user?.id],
    queryFn: async () => {
      if (!user?.id) return { partner: null, pendingInvitations: [], sentInvitations: [] };
      
      // Fetch partner link
      const { data: links, error: linkError } = await supabase
        .from('partner_links')
        .select('*')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .limit(1);

      if (linkError) throw linkError;

      let partner: Partner | null = null;

      if (links && links.length > 0) {
        const link = links[0];
        const partnerId = link.user_id === user.id ? link.partner_id : link.user_id;

        // Fetch partner profile
        const { data: partnerProfile } = await supabase
          .from('profiles')
          .select('id, name')
          .eq('id', partnerId)
          .single();

        if (partnerProfile) {
          partner = {
            id: partnerProfile.id,
            name: partnerProfile.name || 'Partner',
            email: '', // We don't expose email from profiles
            relationshipType: link.relationship_type as RelationshipType,
          };
        }
      }

      // Fetch pending invitations sent to this user's email
      const { data: received } = await supabase
        .from('partner_invitations')
        .select('*')
        .eq('invitee_email', user.email || '')
        .eq('status', 'pending')
        .neq('inviter_id', user.id)
        .gt('expires_at', new Date().toISOString());

      const pendingInvitations = received ? received.map(inv => ({
        id: inv.id,
        inviterId: inv.inviter_id,
        inviteeEmail: inv.invitee_email,
        relationshipType: inv.relationship_type as RelationshipType,
        status: inv.status as InvitationStatus,
        message: inv.message ?? undefined,
        expiresAt: inv.expires_at,
        createdAt: inv.created_at,
      })) : [];

      // Fetch invitations sent by this user
      const { data: sent } = await supabase
        .from('partner_invitations')
        .select('*')
        .eq('inviter_id', user.id);

      const sentInvitations = sent ? sent.map(inv => ({
        id: inv.id,
        inviterId: inv.inviter_id,
        inviteeEmail: inv.invitee_email,
        relationshipType: inv.relationship_type as RelationshipType,
        status: inv.status as InvitationStatus,
        message: inv.message ?? undefined,
        expiresAt: inv.expires_at,
        createdAt: inv.created_at,
      })) : [];

      return { partner, pendingInvitations, sentInvitations };
    },
    enabled: !!user?.id,
  });

  const partner = samplePartner ?? partnerData?.partner ?? null;

  // 2. Query for Partner Holdings
  const { data: partnerHoldingsData, isLoading: holdingsLoading, refetch: fetchPartnerHoldings } = useQuery({
    queryKey: ['partnerHoldings', partner?.id],
    queryFn: async () => {
      if (!partner?.id) return [];
      const { data, error } = await supabase
        .from('holdings')
        .select('*')
        .eq('user_id', partner.id);

      if (error) {
        console.error('Error fetching partner holdings:', error);
        throw error;
      }

      return (data || []).map(transformHolding);
    },
    enabled: !!partner?.id && currentView === 'household' && !samplePartner,
  });

  const partnerHoldings = sampleHoldings.length > 0 ? sampleHoldings : (partnerHoldingsData ?? []);
  const pendingInvitations = partnerData?.pendingInvitations ?? [];
  const sentInvitations = partnerData?.sentInvitations ?? [];

  // Mutations
  const sendInvitationMutation = useMutation({
    mutationFn: async ({ email, relationshipType, message }: { email: string; relationshipType: RelationshipType; message?: string }) => {
      if (!user) throw new Error('Not authenticated');
      if (email === user.email) throw new Error("Cannot invite self");

      const { error } = await supabase
        .from('partner_invitations')
        .insert({
          inviter_id: user.id,
          invitee_email: email,
          relationship_type: relationshipType,
          message,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Invitation sent');
      queryClient.invalidateQueries({ queryKey: ['partner', user?.id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!user) throw new Error('Not authenticated');

      // Get invitation details
      const { data: invitation, error: fetchError } = await supabase
        .from('partner_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (fetchError) throw fetchError;

      // Update status
      const { error: updateError } = await supabase
        .from('partner_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // Create links
      const { error: linkError1 } = await supabase
        .from('partner_links')
        .insert({
          user_id: user.id,
          partner_id: invitation.inviter_id,
          relationship_type: invitation.relationship_type,
          invitation_id: invitationId,
        });
      if (linkError1) throw linkError1;

      const { error: linkError2 } = await supabase
        .from('partner_links')
        .insert({
          user_id: invitation.inviter_id,
          partner_id: user.id,
          relationship_type: invitation.relationship_type,
          invitation_id: invitationId,
        });
      if (linkError2) throw linkError2;
    },
    onSuccess: () => {
      toast.success('Partner invitation accepted!');
      queryClient.invalidateQueries({ queryKey: ['partner', user?.id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const declineInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('partner_invitations')
        .update({ status: 'declined' })
        .eq('id', invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Invitation declined');
      queryClient.invalidateQueries({ queryKey: ['partner', user?.id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('partner_invitations')
        .delete()
        .eq('id', invitationId)
        .eq('inviter_id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Invitation cancelled');
      queryClient.invalidateQueries({ queryKey: ['partner', user?.id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const removePartnerMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('partner_links')
        .delete()
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Partner removed');
      setCurrentView('individual');
      queryClient.invalidateQueries({ queryKey: ['partner', user?.id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // Sample data handlers
  const setSamplePartner = useCallback((partnerData: Partner, holdingsData: Holding[]) => {
    contextSetSamplePartner(partnerData, holdingsData);
    setCurrentView('household');
  }, [contextSetSamplePartner]);

  const clearSamplePartner = useCallback(() => {
    if (samplePartner) {
      contextClearSamplePartner();
      setCurrentView('individual');
    }
  }, [samplePartner, contextClearSamplePartner]);

  return {
    partner,
    partnerHoldings,
    pendingInvitations,
    sentInvitations,
    currentView,
    setCurrentView,
    loading: partnerLoading || (currentView === 'household' && holdingsLoading),
    
    sendInvitation: async (email: string, relationshipType?: RelationshipType, message?: string) => {
      try {
        await sendInvitationMutation.mutateAsync({ email, relationshipType: relationshipType || 'partner', message });
        return { error: null };
      } catch (e) {
        return { error: e as Error };
      }
    },
    acceptInvitation: async (id: string) => {
      try {
        await acceptInvitationMutation.mutateAsync(id);
        return { error: null };
      } catch (e) {
        return { error: e as Error };
      }
    },
    declineInvitation: async (id: string) => {
      try {
        await declineInvitationMutation.mutateAsync(id);
        return { error: null };
      } catch (e) {
        return { error: e as Error };
      }
    },
    cancelInvitation: async (id: string) => {
      try {
        await cancelInvitationMutation.mutateAsync(id);
        return { error: null };
      } catch (e) {
        return { error: e as Error };
      }
    },
    removePartner: async () => {
      try {
        await removePartnerMutation.mutateAsync();
        return { error: null };
      } catch (e) {
        return { error: e as Error };
      }
    },
    
    refetch: refetchPartner,
    fetchPartnerHoldings, // Expose for manual refetch if needed
    setSamplePartner,
    clearSamplePartner,
  };
}
